import { useState, useEffect, useRef } from 'react';
import { get } from '../../utils/apiClient';
import { transformMedicationsForForm, formatMedicationsAsText } from '../../features/notes/utils/chartDataHelpers';
import { logger } from "../../utils/logger";
import { resolveSchemaFields } from './schemaCompat';

const resolveKey = (field) => field.field_id || field.name;

const AUTO_FILL_SOURCES = new Set([
  'patient_diagnoses',
  'patient_medications',
  'patient_supplements',
  'treatment_plan',
  'treatment_plan_objectives',
]);

function getAutoFillSource(field) {
  if (field.auto_fill_from) return field.auto_fill_from;
  if (field.type === 'medication_list') return 'patient_medications';
  if (field.type === 'supplement_list') return 'patient_supplements';
  return null;
}

function shouldAutoFillField(field) {
  return field.auto_fill_from_chart || AUTO_FILL_SOURCES.has(getAutoFillSource(field));
}

function collectAutoFillFields(fields) {
  const result = [];
  if (!fields) return result;
  for (const field of fields) {
    if (shouldAutoFillField(field)) {
      result.push(field);
    }
    if (field.fields) {
      result.push(...collectAutoFillFields(field.fields));
    }
  }
  return result;
}

function hasExistingValue(value) {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

async function safeFetchJson(url, label) {
  try {
    const resp = await get(url);
    if (!resp.ok) {
      logger.warn(`[useChartData] ${label} returned ${resp.status}`);
      return null;
    }
    const ct = resp.headers.get('content-type');
    if (ct && ct.includes('text/html')) {
      logger.warn(`[useChartData] ${label} returned HTML instead of JSON`);
      return null;
    }
    return await resp.json();
  } catch (err) {
    logger.warn(`[useChartData] ${label} fetch failed:`, err.message);
    return null;
  }
}

function normalizeDiagnoses(data) {
  const all = (data.diagnoses || []).map(dx => ({
    diagnosis_id: dx.diagnosis_id || dx.id,
    icd_code_id: dx.icd_code_id || dx.icd_code?.id,
    icd_code: dx.code || dx.icd_code?.code || '',
    description: dx.description || dx.icd_code?.description || '',
    category: dx.category || dx.icd_code?.category || '',
    is_primary: dx.is_primary || false,
    status: dx.status || 'active',
  }));
  // Deduplicate by icd_code_id — keep first occurrence (preserves is_primary)
  const seen = new Set();
  return all.filter(dx => {
    if (seen.has(dx.icd_code_id)) return false;
    seen.add(dx.icd_code_id);
    return true;
  });
}

export const useChartData = (patientId, practitionerId, template, setFormData, initialData = {}, caseId = null, allowChartPrefill = true) => {
  const [originalChartData, setOriginalChartData] = useState({});
  const fetchIdRef = useRef(0);
  const initialDataRef = useRef(initialData);

  useEffect(() => {
    initialDataRef.current = initialData;
  }, [initialData]);

  useEffect(() => {
    const fetchChartData = async () => {
      const schemaFields = resolveSchemaFields(template?.template_schema);
      if (!patientId || !schemaFields.length) return;

      const currentFetchId = ++fetchIdRef.current;
      const autoFillFields = collectAutoFillFields(schemaFields);
      if (autoFillFields.length === 0) return;

      const currentInitialData = initialDataRef.current;
      const chartData: Record<string, unknown> = {};
      const parentSnapshot: Record<string, unknown> = {};

      const fieldsToFetch = [];
      for (const f of autoFillFields) {
        const fk = resolveKey(f);
        const val = currentInitialData[fk];
        if (hasExistingValue(val)) {
          // Preserve existing values in form state; only track them for change detection.
          parentSnapshot[fk] = val;
        } else {
          fieldsToFetch.push(f);
        }
      }

      if (fieldsToFetch.length === 0 || !allowChartPrefill) {
        // Prefill disabled (signed/amendment notes): existing values still
        // seed the change-detection baseline, but empty fields stay empty —
        // current chart data is pulled only via an explicit provider request.
        setOriginalChartData(parentSnapshot);
        return;
      }

      const medsUrl = `/medications/patients/${patientId}/medications`;
      let allMedsCache = null;

      const fetchAllMeds = async () => {
        if (allMedsCache !== null) return allMedsCache;
        const data = await safeFetchJson(medsUrl, 'medications');
        allMedsCache = data ? (data.medications || []) : [];
        return allMedsCache;
      };

      for (const field of fieldsToFetch) {
        if (currentFetchId !== fetchIdRef.current) return;
        try {
          const autoFillSource = getAutoFillSource(field);
          if (autoFillSource === 'patient_diagnoses') {
            const data = await safeFetchJson(
              `/diagnoses/patients/${patientId}/diagnoses?active_only=true`,
              'diagnoses'
            );
            if (data) {
              chartData[resolveKey(field)] = normalizeDiagnoses(data);
            }
          } else if (autoFillSource === 'patient_medications') {
            const allMeds = await fetchAllMeds();
            const medications = allMeds.filter(m => m.item_type !== 'supplement');
            if (field.type === 'textarea') {
              chartData[resolveKey(field)] = formatMedicationsAsText(medications, { includeOutside: true });
            } else {
              chartData[resolveKey(field)] = transformMedicationsForForm(medications, {
                includeOutside: true,
                includeHistory: true,
              });
            }
          } else if (autoFillSource === 'patient_supplements') {
            const allMeds = await fetchAllMeds();
            const supplements = allMeds.filter(m => m.item_type === 'supplement');
            if (field.type === 'textarea') {
              chartData[resolveKey(field)] = formatMedicationsAsText(supplements, { includeOutside: true });
            } else {
              chartData[resolveKey(field)] = transformMedicationsForForm(supplements, {
                includeOutside: true,
                includeHistory: true,
              });
            }
          } else if (autoFillSource === 'treatment_plan' && practitionerId && caseId) {
            const tpUrl = `/treatment-plans?patient_id=${patientId}&practitioner_id=${practitionerId}&case_id=${caseId}`;
            const data = await safeFetchJson(tpUrl, 'treatment-plan');
            if (data) {
              chartData[resolveKey(field)] = data.prescribed_frequency || '';
            }
          } else if (autoFillSource === 'treatment_plan_objectives' && practitionerId && caseId) {
            const tpoUrl = `/treatment-plans?patient_id=${patientId}&practitioner_id=${practitionerId}&case_id=${caseId}`;
            const data = await safeFetchJson(tpoUrl, 'treatment-plan-objectives');
            if (data) {
              chartData[resolveKey(field)] = data.objectives || [];
            }
          }
        } catch (error) {
          logger.error(`Error fetching ${resolveKey(field)}:`, error);
        }
      }

      if (currentFetchId !== fetchIdRef.current) return;
      const originalChartDataUpdate = { ...parentSnapshot, ...chartData };
      setOriginalChartData(originalChartDataUpdate);
      if (fieldsToFetch.length > 0) {
        setFormData(prev => {
          const updates: Record<string, unknown> = {};
          for (const f of fieldsToFetch) {
            const fk = resolveKey(f);
            if (!(fk in chartData)) continue;
            const prevVal = prev[fk];
            const initialVal = currentInitialData[fk];
            // Only apply auto-fetched chart data if the user has not edited this field
            // since the fetch started.
            if (
              prevVal === initialVal ||
              prevVal === undefined ||
              prevVal === null ||
              prevVal === ''
            ) {
              updates[fk] = chartData[fk];
            }
          }
          if (Object.keys(updates).length === 0) return prev;
          return { ...prev, ...updates };
        });
      }
    };

    fetchChartData();
  }, [patientId, practitionerId, caseId, template, setFormData, allowChartPrefill]);

  return { originalChartData, setOriginalChartData };
};
